import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fieldLabel } from './fieldLabels';
import './MissingFieldsModal.css';

// Map each missing field key to where it lives in the app.
// Returns null for contract-local fields (Project_naam etc.) — user fixes those
// by closing this modal and clicking the row's edit pencil.
const FIELD_DEST = {
  Naam:         { path: '/dashboard/workers',           label: 'Medewerkers' },
  Bedrijfsnaam: { path: '/dashboard/workers',           label: 'Medewerkers' },
  Kvk:          { path: '/dashboard/workers',           label: 'Medewerkers' },
  Btw_nummer:   { path: '/dashboard/workers',           label: 'Medewerkers' },
  Plaats:       { path: '/dashboard/workers',           label: 'Medewerkers' },
  Postcode:     { path: '/dashboard/workers',           label: 'Medewerkers' },
  Adres:        { path: '/dashboard/workers',           label: 'Medewerkers' },
  Functiegroep: { path: '/dashboard/workers',           label: 'Medewerkers' },
  Bedrag_1:     { path: '/dashboard/workers',           label: 'Medewerkers (functiegroep)' },
  Bedrag_2:     { path: '/dashboard/workers',           label: 'Medewerkers (functiegroep)' },
  Bedrag_3:     { path: '/dashboard/workers',           label: 'Medewerkers (functiegroep)' },
  Bedrag_4:     { path: '/dashboard/workers',           label: 'Medewerkers (functiegroep)' },
  Bedrag_5:     { path: '/dashboard/workers',           label: 'Medewerkers (functiegroep)' },
  Bedrag_6:     { path: '/dashboard/workers',           label: 'Medewerkers (functiegroep)' },
  Bedrag_7:     { path: '/dashboard/workers',           label: 'Medewerkers (functiegroep)' },
  Opdrachtgever_naam:              { path: '/dashboard/zzp-opdrachtgevers', label: 'Opdrachtgevers' },
  Opdrachtgever_plaats:            { path: '/dashboard/zzp-opdrachtgevers', label: 'Opdrachtgevers' },
  Opdrachtgever_postcode:          { path: '/dashboard/zzp-opdrachtgevers', label: 'Opdrachtgevers' },
  Opdrachtgever_adres:             { path: '/dashboard/zzp-opdrachtgevers', label: 'Opdrachtgevers' },
  Opdrachtgever_vertegenwoordiger: { path: '/dashboard/zzp-opdrachtgevers', label: 'Opdrachtgevers' },
};

// Group {fieldKey -> destination} into a list of {dest, fields[]}.
// Fields without a destination land in a "contract" bucket (no nav button).
function groupByDest(fieldKeys) {
  const buckets = new Map();
  const local = [];
  for (const k of fieldKeys) {
    const d = FIELD_DEST[k];
    if (!d) { local.push(k); continue; }
    const key = d.path;
    if (!buckets.has(key)) buckets.set(key, { ...d, fields: [] });
    buckets.get(key).fields.push(k);
  }
  return { groups: Array.from(buckets.values()), local };
}

export default function MissingFieldsModal({ open, onClose, missing = [], perWorker = null, title }) {
  const navigate = useNavigate();
  if (!open) return null;

  const goTo = (path) => {
    onClose();
    navigate(path);
  };

  const renderGroup = (group) => (
    <div key={group.path} className="mfm-group">
      <div className="mfm-group-head">
        <span className="mfm-group-label">{group.label}</span>
        <button className="mfm-go-btn" onClick={() => goTo(group.path)}>
          Openen <ExternalLink size={13} />
        </button>
      </div>
      <ul className="mfm-field-list">
        {group.fields.map((k) => <li key={k}>{fieldLabel(k)}</li>)}
      </ul>
    </div>
  );

  const renderLocal = (fields) => fields.length > 0 && (
    <div className="mfm-group">
      <div className="mfm-group-head">
        <span className="mfm-group-label">Op dit contract</span>
        <span className="mfm-go-hint">Sluit deze melding en klik op het potlood-icoon van het contract.</span>
      </div>
      <ul className="mfm-field-list">
        {fields.map((k) => <li key={k}>{fieldLabel(k)}</li>)}
      </ul>
    </div>
  );

  return (
    <div className="mfm-overlay" onClick={onClose}>
      <div className="mfm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mfm-header">
          <div className="mfm-header-title">
            <AlertTriangle size={20} className="mfm-warn-icon" />
            <h3>{title || 'Kan niet genereren'}</h3>
          </div>
          <button className="mfm-close" onClick={onClose} aria-label="Sluiten">
            <X size={18} />
          </button>
        </div>

        <div className="mfm-body">
          <p className="mfm-intro">
            Vul eerst de onderstaande velden in. Klik op <strong>Openen</strong> om direct
            naar de juiste tab te gaan.
          </p>

          {perWorker ? (
            <ul className="mfm-worker-list">
              {perWorker.map((d) => {
                const { groups, local } = groupByDest(d.missing || []);
                return (
                  <li key={d.worker_id} className="mfm-worker-item">
                    <div className="mfm-worker-name">
                      {d.worker_naam || `Medewerker #${d.worker_id}`}
                    </div>
                    {groups.map(renderGroup)}
                    {renderLocal(local)}
                  </li>
                );
              })}
            </ul>
          ) : (
            (() => {
              const { groups, local } = groupByDest(missing);
              return (
                <>
                  {groups.map(renderGroup)}
                  {renderLocal(local)}
                </>
              );
            })()
          )}
        </div>

        <div className="mfm-footer">
          <button className="zzp-primary-btn" onClick={onClose}>
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
